import { QueryClient } from '@tanstack/react-query';

/**
 * O cache do React Query, em módulo próprio — e não mais criado dentro do
 * `main.tsx`.
 *
 * ## Por que ele saiu de lá: o cache sobrevivia à sessão
 *
 * Defeito de segurança relatado pelo dono em 2026-09-21 ("quando desloga e
 * loga, a lista de sessão não atualiza de acordo com as permissões"), e a
 * causa não estava no servidor: `sair()` limpava só `acesso`, `expiraEm` e
 * `usuario`, e **nada em todo o front chamava `clear()`**. Trocando de conta,
 * o cache ainda tinha o `GET /sessoes` da conta anterior, e o painel
 * desenhava a lista do usuário ANTIGO até o refetch voltar — mostrando, por
 * alguns instantes, conversas que o escopo do servidor nega.
 *
 * É a garantia inteira do ADR-0019 desmentida pelo cliente: o `filtroSessoes`
 * recortava certo, a resposta nova vinha certa, e a tela mostrava dado velho.
 * Fica registrado porque é o tipo de defeito que nenhum teste de API pegaria
 * — o servidor estava correto o tempo todo.
 *
 * ## Por que aqui, e não um singleton escondido
 *
 * O store é Zustand puro e não tem acesso ao `queryClient`. As saídas eram:
 * um registrador global (o cache se anuncia ao store), que é singleton
 * escondido com ordem de inicialização implícita; ou um `subscribe` no
 * `main.tsx`, que deixa a limpeza fora do gesto que a exige e some nos testes
 * de unidade. A terceira é esta: o cache vira um MÓDULO, e o store o importa
 * como qualquer outro. A aresta é explícita, vai numa direção só
 * (`sessao.ts` → `cache.ts`, e `cache.ts` não importa nada nosso) e o
 * compilador a enxerga.
 */
export const clienteQuery = new QueryClient({
  // `refetchOnWindowFocus` ligado por causa do aparelho que dorme: voltar à
  // tela é o momento em que o painel mais provavelmente está desatualizado.
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: true, staleTime: 30_000 } },
});

/**
 * Esvazia tudo o que a sessão anterior deixou no cache.
 *
 * `clear()`, e não `invalidateQueries()`: invalidar marca como velho mas
 * **mantém o dado servindo** enquanto o refetch não volta — que é exatamente
 * a janela do defeito. `clear()` apaga, e a tela desenha o estado de carga
 * até a resposta nova chegar.
 *
 * Chamado nos DOIS lados da troca, e os dois são necessários:
 *
 * - em `sair()`, porque é o gesto que encerra a sessão (inclusive a saída
 *   forçada por 401 e por `conta.derrubada`);
 * - em `entrar()`, porque `clear()` **não cancela requisição em voo**. Uma
 *   consulta disparada pela conta antiga que só resolva DEPOIS do `sair()`
 *   repovoa o cache com a resposta velha, e aí já é tarde. Limpar de novo ao
 *   entrar fecha essa corrida — e cobre também o caminho em que alguém entra
 *   sem passar por `sair()` (sessão restaurada pelo refresh na rota raiz).
 */
export function esvaziarCache(): void {
  clienteQuery.clear();
}

/**
 * O que MAIS precisa morrer junto com a conta, além do cache de consultas.
 *
 * Os envios de anexo (`sessoes/envios.ts`) vivem fora de qualquer componente —
 * é o que permite o upload de 1 GB seguir quando a pessoa troca de aba ou de
 * conversa —, e por isso sobrevivem a tudo, inclusive à troca de conta: o
 * arquivo que a conta A estava subindo, e a falha de entrega que ela ainda não
 * tinha visto, apareceriam para a conta B. Mesma família do defeito de
 * 2026-09-21, outro estado.
 *
 * Registro, e não um `import` de `cache.ts` para `envios.ts`: o `envios` importa
 * `api.ts`, que importa o store da sessão, que importa este arquivo — e esta
 * página existe justamente para NÃO importar nada nosso. Quem tem estado fora
 * de componente se registra aqui. Se o módulo nunca foi carregado, não há nada
 * a limpar, então a ordem de inicialização não importa.
 *
 * Só em `sair()`, e NUNCA em `entrar()`: o `entrar` roda a cada renovação do
 * token (15 min) — o `esvaziarCache` ali já é um custo conhecido, mas abortar
 * um upload de 5 GiB a cada renovação o tornaria impossível.
 */
const aoSair = new Set<() => void>();

export function limparAoSair(limpeza: () => void): void {
  aoSair.add(limpeza);
}

export function executarLimpezasDaSaida(): void {
  for (const limpeza of aoSair) limpeza();
}
